import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Anchor, Button, Card, Group, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FORMAT_RULES } from '@quizee/core';
import { Link, useParams } from 'react-router-dom';
import { ApiError, api, errorMessage } from '../api/client';
import type {
  BrandConfig,
  EmailList,
  Interaction,
  InteractionResults,
  PublishResult,
} from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ApprovalPanel, ProvenanceBadge, ProvenanceNote } from '../components/ApprovalPanel';
import { EmbedSnippet } from '../components/EmbedSnippet';
import { ErrorNote, ProblemList } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { InteractionFields } from '../components/InteractionFields';
import { InteractionReadout } from '../components/InteractionReadout';
import { ReadEdit } from '../components/ReadEdit';
import { ResultsPanel } from '../components/ResultsPanel';
import { StatusBadge } from '../components/StatusBadge';
import { sameDraft } from '../edit/same';
import { formatLabel, whenLabel } from '../format';
import {
  buildInteractionContent,
  draftFromContent,
  type InteractionDraft,
} from '../interaction/draft';
import { newsletterContext } from '../interaction/newsletter';
import { awaitingApproval } from '../interaction/provenance';

/**
 * One interaction: what it says, how it is doing, and the way into changing it.
 *
 * It opens read only, like every settings screen, and for the same reason plus a stronger one. A
 * published version is immutable: what readers are looking at right now cannot be edited, only
 * replaced by publishing a new version. A screen that opened as a live form over a live poll said
 * the opposite of that, and quietly, since the fields showed the current wording and a keystroke
 * looked like a correction rather than the first line of a new version.
 *
 * So the interaction is read, and Edit says what editing is: writing the next version, which
 * readers meet when somebody publishes it and not before. The builder underneath is untouched by
 * any of this. Writing a quiz is what it is for, and it appears the moment somebody asks to write
 * one.
 *
 * Saving and publishing report through notifications; anything that failed stays on the page. A
 * confirmation is worth a second and then it should get out of the way. A problem is worth reading
 * twice, next to the thing that caused it.
 */
export function InteractionEditor() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const { id } = useParams<{ id: string }>();
  const interactionId = id ?? '';

  const [interaction, setInteraction] = useState<Interaction | null>(null);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  /**
   * The brand this interaction runs on, read for one thing: the newsletter.
   *
   * The signup step lives on the interaction and everything it needs to be honest about lives on
   * the masthead, so the wording a reader would agree to and what happens to their address are
   * read from there rather than guessed at here. Null while it is loading or if it could not be
   * read, and in both cases the step is simply not offered: a switch beside a sentence we invented
   * would be worse than no switch.
   */
  const [brand, setBrand] = useState<BrandConfig | null>(null);
  /**
   * The brand's lists, for the signup step's picker. Empty rather than null while they have not
   * loaded, or if they could not be read, so the picker still offers the default list rather than
   * disappearing: a signup step that vanished because a second, unrelated request failed would be
   * a strange thing for an editor to have to work out.
   */
  const [lists, setLists] = useState<readonly EmailList[]>([]);
  /**
   * What is on screen while somebody is editing, or null while the interaction is only being read.
   *
   * The form keeps its own copy inside ReadEdit; this is the same draft passed back out, for the
   * two things drawn outside the form that have to know about it: publishing, which is not offered
   * while a version is being written, and the approval panel, which says when an approved draft
   * has been changed since somebody put their name to it.
   */
  const [editing, setEditing] = useState<InteractionDraft | null>(null);

  const [problems, setProblems] = useState<readonly string[]>([]);

  const [approving, setApproving] = useState(false);
  const [approveProblem, setApproveProblem] = useState<string | null>(null);

  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState<PublishResult | null>(null);
  /**
   * Publishing was refused because the brand has no website on it, which is the one publish
   * failure an editor cannot act on from this screen. The sentence gets a link to the screen that
   * fixes it rather than leaving them to find it.
   */
  const [needsDomain, setNeedsDomain] = useState<string | null>(null);

  const [results, setResults] = useState<InteractionResults | null>(null);
  const [resultsProblem, setResultsProblem] = useState<string | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);

  const load = useCallback(() => {
    if (orgId === null || interactionId === '') return;
    setLoadProblem(null);
    setInteraction(null);
    api
      .interaction(orgId, interactionId)
      .then((body) => setInteraction(body.interaction))
      .catch((error: unknown) => setLoadProblem(errorMessage(error)));
  }, [interactionId, orgId]);

  useEffect(load, [load]);

  const brandId = interaction?.brand_id ?? null;

  useEffect(() => {
    setBrand(null);
    if (orgId === null || brandId === null) return;
    let cancelled = false;
    api
      .brand(orgId, brandId)
      .then((body) => {
        if (!cancelled) setBrand(body);
      })
      .catch(() => {
        // The interaction is still readable without it. The newsletter step is the only thing
        // that needs the brand, and it stays out of the way rather than failing the screen.
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, orgId]);

  useEffect(() => {
    setLists([]);
    if (orgId === null || brandId === null) return;
    let cancelled = false;
    api
      .lists(orgId, brandId)
      .then((body) => {
        if (!cancelled) setLists(body.lists);
      })
      .catch(() => {
        // Allowed to fail quietly, like the brand read above: the signup step still works, with
        // nothing to pick beyond the brand's default.
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, orgId]);

  /**
   * The saved content as a draft, made once per interaction.
   *
   * Once, because reading content into a draft mints fresh row keys each time, and two drafts of
   * the same content built separately would never compare equal. This is what a form is seeded
   * from and what an open form is compared against, so both sides are the same object and "has
   * anything actually changed" is a question about values rather than about identity.
   */
  const savedDraft = useMemo(
    () => (interaction === null ? null : draftFromContent(interaction.content)),
    [interaction],
  );

  const refreshResults = useCallback(() => {
    if (orgId === null || interactionId === '') return;
    setResultsProblem(null);
    setResultsLoading(true);
    api
      .results(orgId, interactionId)
      .then((body) => setResults(body))
      .catch((error: unknown) => setResultsProblem(errorMessage(error)))
      .finally(() => setResultsLoading(false));
  }, [interactionId, orgId]);

  const isLive = typeof interaction?.live_version === 'number';

  useEffect(() => {
    if (isLive) refreshResults();
  }, [isLive, refreshResults]);

  /**
   * The masthead's newsletter settings, or null while they are unknown.
   *
   * Undefined rather than null is what `buildInteractionContent` wants for the second case, so a
   * save is never refused over a wording nobody could look up.
   */
  const newsletter = brand === null ? null : newsletterContext(brand, lists);
  const inheritedNewsletterConsent = newsletter?.consent.value;

  /** What the builder would refuse, asked before anything is sent, in an editor's sentences. */
  function check(draft: InteractionDraft): readonly string[] {
    if (interaction === null) return [];
    const built = buildInteractionContent(draft, interaction.content, inheritedNewsletterConsent);
    return built.ok ? [] : built.errors;
  }

  async function save(draft: InteractionDraft): Promise<boolean> {
    if (orgId === null || interaction === null) return false;

    const built = buildInteractionContent(draft, interaction.content, inheritedNewsletterConsent);
    if (!built.ok) {
      setProblems(built.errors);
      return false;
    }

    setProblems([]);
    try {
      const body = await api.saveInteraction(orgId, interactionId, built.content);
      setInteraction(body.interaction);
      notifications.show({ message: 'Saved.', color: 'violet' });
      return true;
    } catch (error) {
      setProblems([errorMessage(error)]);
      return false;
    }
  }

  /**
   * Approving.
   *
   * The reply carries the interaction with the approval on it, and it replaces what is held here,
   * but any draft being edited is left exactly as it is: approving is not a save, and it must not
   * throw away words somebody has typed and not yet saved.
   */
  async function approve() {
    if (orgId === null) return;
    setApproveProblem(null);
    setApproving(true);
    try {
      const body = await api.approveInteraction(orgId, interactionId);
      setInteraction(body.interaction);
      notifications.show({ message: 'Approved. You can publish this now.', color: 'violet' });
    } catch (error) {
      setApproveProblem(errorMessage(error));
    } finally {
      setApproving(false);
    }
  }

  async function publish() {
    if (orgId === null) return;
    setProblems([]);
    setNeedsDomain(null);
    setPublishing(true);
    try {
      const result = await api.publishInteraction(orgId, interactionId);
      setPublished(result);
      setInteraction((current) =>
        current === null
          ? current
          : {
              ...current,
              status: 'published',
              live_version: result.version,
              next_version: result.version + 1,
            },
      );
      notifications.show({
        message: `Published. Readers on version ${String(result.version)} from now on.`,
        color: 'violet',
      });
      refreshResults();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'brand_has_no_domains') {
        setNeedsDomain(error.message);
      } else {
        setProblems([errorMessage(error)]);
      }
    } finally {
      setPublishing(false);
    }
  }

  if (loadProblem !== null) {
    return (
      <>
        <Text size="sm" mb="sm">
          <Anchor component={Link} to="/interactions" size="sm">
            Back to interactions
          </Anchor>
        </Text>
        <ErrorNote message={loadProblem} onRetry={load} />
      </>
    );
  }

  if (interaction === null || savedDraft === null) {
    return <Loading label="Loading this interaction" />;
  }

  const liveVersion = interaction.live_version ?? null;
  const nextVersion = interaction.next_version;
  const questionCount = editing?.questions.length ?? interaction.content.questions.length;
  const rules = FORMAT_RULES[interaction.content.format];
  const name = rules.label.toLowerCase();
  // The server refuses to publish an AI draft nobody has approved. So does this screen, in words.
  const blocked = awaitingApproval(interaction);
  const dirty = editing !== null && !sameDraft(editing, savedDraft);

  return (
    <>
      <Text size="sm" mb="sm">
        <Anchor component={Link} to="/interactions" size="sm">
          Back to interactions
        </Anchor>
      </Text>

      <Group justify="space-between" align="center" gap="md">
        <Title order={1} size="h2">
          {interaction.content.title === ''
            ? formatLabel(interaction.content.format)
            : interaction.content.title}
        </Title>
        <Group gap="xs">
          <StatusBadge status={interaction.status} />
          <ProvenanceBadge interaction={interaction} />
        </Group>
      </Group>
      <Text c="dimmed" size="sm" mt="xs" mb="lg">
        {formatLabel(interaction.content.format)}.{' '}
        {liveVersion === null
          ? `Not published yet. Readers cannot see this ${name}.`
          : `Live, version ${String(liveVersion)}.`}{' '}
        Last saved {whenLabel(interaction.updated_at)}.
      </Text>
      <ProvenanceNote interaction={interaction} name={name} />

      <ProblemList problems={problems} />

      {needsDomain !== null && (
        <Alert role="alert" color="red" variant="light" radius="md" my="md">
          <Text size="sm">{needsDomain}</Text>
          <Anchor component={Link} to={`/settings/brands/${interaction.brand_id}`} size="sm">
            Add a website for this brand
          </Anchor>
        </Alert>
      )}

      <section className="editor-content-panel" aria-label="Interaction content">
        <div className="editor-panel-heading">
          <h2>Content</h2>
          <span>
            {questionCount} {questionCount === 1 ? 'question' : 'questions'} ·{' '}
            {editing === null ? 'Saved version' : 'Editing draft'}
          </span>
        </div>
        <ReadEdit<InteractionDraft>
          noun={`this ${name}`}
          canEdit
          open={() => savedDraft}
          openLabel="Edit"
          openName={`Edit this ${name}`}
          saveLabel="Save"
          saveName={`Save this ${name}`}
          busyLabel="Saving..."
          problemsTitle="Before this can be saved:"
          check={check}
          save={save}
          onDraft={setEditing}
          form={(draft, set) => (
            <>
              <Text size="sm" c="dimmed" mb="md">
                {liveVersion === null
                  ? `Nothing is live yet, so this is version ${String(nextVersion)} in the making. Readers see it when you publish it.`
                  : `You are writing version ${String(nextVersion)}. Readers stay on version ${String(liveVersion)} until you publish this one.`}
              </Text>
              <InteractionFields draft={draft} onChange={set} newsletter={newsletter} />
            </>
          )}
        >
          <InteractionReadout content={interaction.content} newsletter={newsletter} />
          <Text size="sm" c="dimmed" mt="lg">
            {liveVersion === null
              ? `Nothing is live yet. Editing changes the draft, and publishing it makes version ${String(nextVersion)}.`
              : `Version ${String(liveVersion)} is live and cannot be changed. Editing writes version ${String(nextVersion)}, and readers stay on version ${String(liveVersion)} until you publish it.`}
          </Text>
        </ReadEdit>
      </section>

      <ApprovalPanel
        interaction={interaction}
        name={name}
        dirty={dirty}
        approving={approving}
        problem={approveProblem}
        onApprove={() => void approve()}
      />

      {/*
       * Publishing belongs to the interaction as it is saved, so it is offered while it is being
       * read and not while a version is being written. That is the whole of the old "save your
       * changes before publishing" rule, made structural rather than said in a note under a
       * disabled button.
       */}
      {editing === null && (
        <Group
          className="editor-publish-bar"
          mt="lg"
          gap="md"
          align="center"
          justify="space-between"
        >
          <div>
            <Text fw={500}>
              {liveVersion === null ? 'Ready for your readers?' : 'Publish your next version'}
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              {liveVersion === null
                ? 'Publish to get the embed code for your article.'
                : `Readers stay on version ${String(liveVersion)} until you publish again.`}
            </Text>
          </div>
          <Button onClick={() => void publish()} disabled={publishing || blocked}>
            {publishing
              ? 'Publishing...'
              : liveVersion === null
                ? 'Publish'
                : `Publish version ${String(nextVersion)}`}
          </Button>
          {blocked && (
            <Text size="sm" c="dimmed">
              A model drafted this {name}, so somebody has to approve it before it can be published.
            </Text>
          )}
        </Group>
      )}

      {published !== null && (
        <EmbedSnippet snippet={published.embed_snippet} liveUrl={published.live_url} />
      )}
      {published === null && liveVersion !== null && (
        <Text c="dimmed" size="sm" mt="lg">
          This {name} is already live. Publish again to see the embed code for the latest version.
        </Text>
      )}

      {liveVersion === null && published === null ? (
        <Card component="section" withBorder radius="lg" p="lg" mt="xl">
          <Title order={2} size="h3">
            {rules.hasResults ? 'Results' : 'Responses'}
          </Title>
          <Text c="dimmed" size="sm" mt="xs">
            {rules.hasResults
              ? `Results appear here once the ${name} is live.`
              : `Answers appear here once the ${name} is live. Readers see no results from a ${name}, only your finishing message.`}
          </Text>
        </Card>
      ) : (
        <ResultsPanel
          content={interaction.content}
          results={results}
          problem={resultsProblem}
          loading={resultsLoading}
          onRefresh={refreshResults}
        />
      )}
    </>
  );
}
